import {
  BoxArrowUp,
  CaretDown,
  CaretUp,
  FilePdf,
  X,
} from "@phosphor-icons/react"
import { getExtension, inr } from "@vesatogo/utils"
import { memo, useState } from "react"
import toast from "react-hot-toast"
import FileUploadButton from "~/components/FIleUploadButton"

const GstPctInput = memo(
  ({
    value,
    onChange,
    disabled,
  }: {
    value: number | undefined
    onChange: (v: number) => void
    disabled?: boolean
  }) => (
    <input
      type="number"
      min={0}
      max={100}
      step={0.01}
      disabled={disabled}
      value={value ?? 0}
      onChange={e => onChange(Number(e.target.value))}
      className={`w-16 text-center border rounded px-1 py-0.5 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-olive-500 ${
        disabled
          ? "bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed"
          : "bg-white text-gray-800 border-gray-300"
      }`}
    />
  )
)

type GstTaxRowProps = {
  name: string
  amount: number
  pct: number | undefined
  field: string
  taxType: "igst" | "cgst" | "sgst"
  autoCalculated: boolean
  isSameState?: boolean
  isDisabled?: boolean
  onPctChange: (field: string, pct: number) => void
}

export const GstTaxRow = memo(
  ({
    name,
    amount,
    pct,
    field,
    taxType,
    autoCalculated,
    isSameState,
    isDisabled,
    onPctChange,
  }: GstTaxRowProps) => {
    const taxTypeDisabled =
      !autoCalculated &&
      isSameState !== undefined &&
      ((isSameState && taxType === "igst") ||
        (!isSameState && (taxType === "cgst" || taxType === "sgst")))

    const inputDisabled = isDisabled || taxTypeDisabled

    return (
      <div className="flex flex-col gap-1 w-full justify-center items-center">
        <div className="flex items-center justify-start gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {name}
          </span>
          {autoCalculated ? (
            <span className="text-xs text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">
              {pct != null ? `${pct}%` : "00"}
            </span>
          ) : taxTypeDisabled ? (
            <span
              title={
                isSameState
                  ? "Not applicable for same-state transactions"
                  : "Not applicable for inter-state transactions"
              }
              className="text-xs text-gray-300 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 cursor-not-allowed"
            >
              {pct != null ? `${pct}%` : "0%"}
            </span>
          ) : (
            <GstPctInput
              value={pct}
              disabled={inputDisabled}
              onChange={v => onPctChange(field, v)}
            />
          )}
        </div>
        <div className="text-sm font-600 text-green-500">{inr(amount)}</div>
      </div>
    )
  }
)

export type GstAttachment = {
  id?: string | number
  media: { id: string; kind?: string; url: string }
}

export type GstBreakdownCardProps = {
  label: string
  autoCalculated: boolean
  onAutoChange: (v: boolean) => void
  isDisabled?: boolean
  autoToggleDisabled?: boolean
  isSameState?: boolean
  totalGst: number
  igst: number
  sgst: number
  cgst: number
  igstPct: number | undefined
  sgstPct: number | undefined
  cgstPct: number | undefined
  igstField: string
  sgstField: string
  cgstField: string
  onPctChange: (field: string, pct: number) => void
  attachments?: GstAttachment[]
  onAddAttachment?: (newOnes: GstAttachment[]) => void
  onRemoveAttachment?: (index: number) => void
}

export const GstBreakdownCard = memo(
  ({
    label,
    autoCalculated,
    onAutoChange,
    isDisabled,
    autoToggleDisabled,
    isSameState,
    totalGst,
    igst,
    sgst,
    cgst,
    igstPct,
    sgstPct,
    cgstPct,
    igstField,
    sgstField,
    cgstField,
    onPctChange,
    attachments = [],
    onAddAttachment,
    onRemoveAttachment,
  }: GstBreakdownCardProps) => {
    const [expanded, setExpanded] = useState(true)

    const isIgstMode =
      isSameState === false ||
      (!autoCalculated && isSameState === undefined && igst > 0) ||
      (autoCalculated && igst > 0)
    const gstLabel = isIgstMode ? "(i.e. IGST)" : "(i.e. CGST + SGST)"

    const stateHint =
      !autoCalculated && isSameState !== undefined ? (
        <p className="text-[10px] text-gray-400 mt-1">
          {isSameState
            ? "Same-state transaction — CGST & SGST apply."
            : "Inter-state transaction — IGST applies."}
        </p>
      ) : null

    return (
      <div className="border rounded-lg overflow-hidden mb-3 shadow-sm">
        <div
          className="flex items-center justify-between px-4 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => setExpanded(v => !v)}
        >
          <div
            className="flex items-center gap-2"
            onClick={e => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={autoCalculated}
              disabled={isDisabled || autoToggleDisabled}
              title={
                autoToggleDisabled && !isDisabled
                  ? "GST must be entered manually"
                  : undefined
              }
              onChange={e => onAutoChange(e.target.checked)}
              className="w-4 h-4 accent-green-500 cursor-pointer disabled:cursor-not-allowed"
              onClick={e => e.stopPropagation()}
            />
            <label
              className="text-sm font-semibold text-gray-700 select-none cursor-pointer"
              onClick={e => e.stopPropagation()}
            >
              Derive {label} GST Breakdown{" "}
              <span className="text-[10px] font-normal text-gray-400">
                {gstLabel}
              </span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-red text-sm font-600">{inr(totalGst)}</span>
            {expanded ? (
              <CaretUp size={14} className="text-gray-400" />
            ) : (
              <CaretDown size={14} className="text-gray-400" />
            )}
          </div>
        </div>

        {expanded && (
          <div className="bg-white px-4 py-3 overflow-x-auto">
            <div className="flex gap-2 w-full px-6 items-center gap-3">
              <GstTaxRow
                name="IGST"
                amount={igst}
                pct={igstPct}
                field={igstField}
                taxType="igst"
                autoCalculated={autoCalculated}
                isSameState={isSameState}
                isDisabled={isDisabled}
                onPctChange={onPctChange}
              />
              <GstTaxRow
                name="CGST"
                amount={cgst}
                pct={cgstPct}
                field={cgstField}
                taxType="cgst"
                autoCalculated={autoCalculated}
                isSameState={isSameState}
                isDisabled={isDisabled}
                onPctChange={onPctChange}
              />
              <GstTaxRow
                name="SGST"
                amount={sgst}
                pct={sgstPct}
                field={sgstField}
                taxType="sgst"
                autoCalculated={autoCalculated}
                isSameState={isSameState}
                isDisabled={isDisabled}
                onPctChange={onPctChange}
              />

              {(onAddAttachment || attachments.length > 0) && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    {!isDisabled && onAddAttachment && (
                      <FileUploadButton
                        multiple
                        method="put"
                        appName="setutrade"
                        environment={import.meta.env.VITE_APP_ENV}
                        endpoint={`https://${
                          import.meta.env.VITE_REST_URL
                        }/rest/trade/uploader/`}
                        media={attachments}
                        buttonIcon={
                          <BoxArrowUp size={16} className="text-green-500" />
                        }
                        buttonProps={{
                          size: "sm",
                        }}
                        buttonLabel="Upload"
                        onError={(msg: string) => toast.error(msg)}
                        accept="image/*,application/pdf"
                        maxFileSize={10 * 1024 * 1024}
                        onSuccess={(messages: any[]) => {
                          const newOnes: GstAttachment[] = messages.map(m => ({
                            media: m.data.media,
                          }))
                          onAddAttachment(newOnes)
                        }}
                      />
                    )}
                    {attachments.length > 0 && (
                      <div className="flex gap-2">
                        {attachments.map((att, i) => {
                          const url = att.media?.url || ""
                          const ext = getExtension(url)?.toLowerCase()
                          const isPdf = ext === "pdf"
                          return (
                            <div
                              key={i}
                              className="relative w-8 h-8 rounded border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center group"
                            >
                              {isPdf ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="flex flex-col items-center gap-0.5"
                                >
                                  <FilePdf size={28} className="text-red-400" />
                                  <span className="text-[9px] text-gray-400">
                                    PDF
                                  </span>
                                </a>
                              ) : (
                                <a href={url} target="_blank" rel="noreferrer">
                                  <img
                                    src={url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                </a>
                              )}
                              {!isDisabled && onRemoveAttachment && (
                                <button
                                  onClick={e => {
                                    e.preventDefault()
                                    onRemoveAttachment(i)
                                  }}
                                  className="absolute top-0.5 right-0.5 bg-white rounded-full p-0.5 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X size={10} className="text-red-500" />
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {stateHint}
          </div>
        )}
      </div>
    )
  }
)
